import { FilterState, useSmartSwapsSearchFilters } from './usePageParams';
import { useEffect, useState } from 'react';
import { useDebouncedState } from 'utils/useDebounce';
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  LiquidityFilter,
  McRangeFilter,
  VolumeRangeFilter,
} from './ResearchFilters';
import { isNotNil } from 'modules/utils/isNotNil';
import { allSuffixes, formatNumberWithSuffix } from 'utils/FormatNumber';
import { TokenLaunchPeriods } from '../Wallets/usePageParams';
import { isValidAddress } from 'helpers/api/web3Utils';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../../components/dropdown/sort-dropdown';
import {
  ActivteFiltersBar,
  FilterTag,
  SelectFiltersBar,
} from '../../../components/filters';
import { twMerge } from 'tailwind-merge';
import TextInput from '../../../components/inputs/text-input/text-input';
import { useSelectedChain } from '../../../components/chains/getChains';

export const TOKENS_LAUNCH_PERIOD_FILTERS = [
  {
    label: '> 1 Hour',
    value: TokenLaunchPeriods.Hour,
  },
  {
    label: '> 1 Day',
    value: TokenLaunchPeriods.Day,
  },
  {
    label: '> 1 Week',
    value: TokenLaunchPeriods.Week,
  },
  {
    label: '> 1 Month',
    value: TokenLaunchPeriods.Month,
  },
  {
    label: '> 3 Months',
    value: TokenLaunchPeriods.ThreeMonths,
  },
];

const STATUS_OPTIONS = [
  {
    value: 'buy' as 'buy' | 'sell',
    label: 'Buy',
    icon: null,
  },
  {
    value: 'sell' as 'buy' | 'sell',
    label: 'Sell',
    icon: null,
  },
];

export const SmartSwapsFiltersBar = () => {
  const { filtersState: filters, setFilters } = useSmartSwapsSearchFilters();
  const [address, debouncedAddress, setAddress] = useDebouncedState('', 250);
  const [walletAddress, debouncedWalletAddress, setWalletAddress] =
    useDebouncedState('', 250);
  const selectedChain = useSelectedChain();
  const [addressError, setAddressError] = useState(false);
  const [walletAddressError, setWalletAddressError] = useState(false);

  const nOfFiltersActive = [
    filters.poolCreatedAt,
    filters.status,
    filters.address,
    filters.walletAddress,
    filters.minVolume,
    filters.maxVolume,
    filters.minMc,
    filters.maxMc,
    filters.minLiquidity,
    filters.maxLiquidity,
  ].filter((value) => value !== undefined && value && value !== '').length;

  useEffect(() => {
    if (debouncedAddress) {
      setAddressError(!isValidAddress(debouncedAddress, selectedChain));
      if (isValidAddress(debouncedAddress, selectedChain)) {
        updateFilters({
          address: debouncedAddress,
        });
      }
    }
  }, [debouncedAddress]);

  useEffect(() => {
    if (debouncedWalletAddress) {
      setWalletAddressError(
        !isValidAddress(debouncedWalletAddress, selectedChain)
      );
      if (isValidAddress(debouncedWalletAddress, selectedChain)) {
        updateFilters({
          walletAddress: debouncedWalletAddress,
        });
      }
    }
  }, [debouncedWalletAddress]);

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setAddress(inputValue);
    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        address: undefined,
      });
    }
  };

  const handleWalletAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setWalletAddress(inputValue);
    if (!inputValue) {
      setWalletAddressError(false);
      updateFilters({
        walletAddress: undefined,
      });
    }
  };

  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filters, ...update });
  };

  return (
    <div className="xxs:mx-1 space-y-1 lg:mx-0">
      <SelectFiltersBar
        className="xxs:justify-start space-x-2"
        icon={
          <div
            className={twMerge(
              'xxs:p-1 flex xxs:space-x-1 items-center',
              nOfFiltersActive
                ? 'xxs:hover:text-dex-white'
                : ' xxs:hover:text-dex-white xxs:text-dex-white-secondary xxs:px-2 '
            )}
          >
            <FunnelIcon aria-hidden="true" width={16} height={16} />
            {nOfFiltersActive ? <div>{nOfFiltersActive}</div> : ''}
          </div>
        }
        isCondensed={true}
        sort={
          <div className="xxs:flex xxs:space-x-2">
            <div>
              <TextInput
                name="address"
                error={addressError ? 'Address not valid' : ''}
                value={address}
                prefix={
                  <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
                }
                onChange={handleAddressInputChange}
                placeholder="Token Address"
              />
            </div>

            <div>
              <TextInput
                name="walletAddress"
                error={walletAddressError ? 'Address not valid' : ''}
                value={walletAddress}
                prefix={
                  <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
                }
                onChange={handleWalletAddressInputChange}
                placeholder="Wallet Address"
              />
            </div>
          </div>
        }
      >
        <div className="xxs:space-y-2">
          <div className="xxs:hidden lg:block xxs:text-dex-white-secondary">
            Use advanced filters to find the best tokens.
          </div>
          <div>
            <TextInput
              name="address"
              error={addressError ? 'Address not valid' : ''}
              value={address}
              prefix={
                <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
              }
              onChange={handleAddressInputChange}
              placeholder="Token Address"
            />
          </div>

          <div>
            <TextInput
              name="walletAddress"
              error={walletAddressError ? 'Address not valid' : ''}
              value={walletAddress}
              prefix={
                <MagnifyingGlassIcon className="w-4 text-dex-white-secondary" />
              }
              onChange={handleWalletAddressInputChange}
              placeholder="Wallet Address"
            />
          </div>

          <StatusFilter
            value={filters.status as 'buy' | 'sell'}
            onChange={(value: 'buy' | 'sell') => {
              if (value === filters.status) {
                updateFilters({
                  status: undefined as any,
                });
              } else {
                updateFilters({
                  status: value,
                });
              }
            }}
          />

          <TokenLaunchPeriodFilterMobile
            value={filters.poolCreatedAt as TokenLaunchPeriods}
            onChange={(value: TokenLaunchPeriods) => {
              if (value === filters.poolCreatedAt) {
                updateFilters({
                  poolCreatedAt: undefined as any,
                });
              } else {
                updateFilters({
                  poolCreatedAt: value,
                });
              }
            }}
          />
          <McRangeFilter />
          <LiquidityFilter />
          <VolumeRangeFilter />
        </div>
      </SelectFiltersBar>

      <ActivteFiltersBar>
        {filters.poolCreatedAt && (
          <FilterTag
            onRemove={() => {
              setAddress('');
              return updateFilters({
                poolCreatedAt: undefined,
              });
            }}
            label={
              `Age: ${
                TOKENS_LAUNCH_PERIOD_FILTERS.filter(
                  (period) => period.value === filters.poolCreatedAt
                )?.[0]?.label
              }` || ''
            }
          />
        )}
        {filters.status && (
          <FilterTag
            onRemove={() => {
              setAddress('');
              return updateFilters({
                status: undefined,
              });
            }}
            label={`Side: ${filters?.status?.toUpperCase()}` || ''}
          />
        )}
        {filters.address && (
          <FilterTag
            onRemove={() => {
              setAddress('');
              return updateFilters({
                address: undefined,
              });
            }}
            label={`Token: ${filters?.address ?? address ?? '?'}`}
          />
        )}
        {filters.walletAddress && (
          <FilterTag
            onRemove={() => {
              setAddress('');
              return updateFilters({
                walletAddress: undefined,
              });
            }}
            label={`Wallet: ${filters?.walletAddress ?? walletAddress ?? '?'}`}
          />
        )}
        {isNotNil(filters.minVolume) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minVolume: undefined,
              })
            }
            label={`Volume > $${formatNumberWithSuffix(filters.minVolume, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.maxVolume) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxVolume: undefined,
              })
            }
            label={`Volume < $${formatNumberWithSuffix(filters.maxVolume, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.minMc) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minMc: undefined,
              })
            }
            label={`MC > $${formatNumberWithSuffix(filters.minMc, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.maxMc) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxMc: undefined,
              })
            }
            label={`MC < $${formatNumberWithSuffix(filters.maxMc, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.minLiquidity) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minLiquidity: undefined,
              })
            }
            label={`Liq. > $${formatNumberWithSuffix(filters.minLiquidity, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
        {isNotNil(filters.maxLiquidity) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxLiquidity: undefined,
              })
            }
            label={`Liq. < $${formatNumberWithSuffix(filters.maxLiquidity, {
              suffixes: allSuffixes,
              precision: 0,
            })}`}
          />
        )}
      </ActivteFiltersBar>
    </div>
  );
};

function TokenLaunchPeriodFilterMobile(
  props: Omit<SortDropdownProps<TokenLaunchPeriods>, 'options'>
) {
  return (
    <SortDropdown
      className="xxs:py-2 xxs:px-3 h-auto"
      {...props}
      options={TOKENS_LAUNCH_PERIOD_FILTERS}
      mainLabel="Token Age"
    />
  );
}

function StatusFilter(
  props: Omit<SortDropdownProps<'buy' | 'sell'>, 'options'>
) {
  return (
    <SortDropdown
      className="xxs:py-2 xxs:px-3 h-auto"
      {...props}
      options={STATUS_OPTIONS}
      mainLabel="Side"
    />
  );
}
